import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Avatar, lighten, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { Qualifications } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useContactDrawerStore from 'stores/contact-drawer-store';
import { makeStyles } from 'tss-react/mui';
import PaiRoundTextField from '../../../../../ui/base-components/pai-round-text-field';

const DataNotAvailable = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Typography className={classes.notAvailableText}>{t('dataNotAvailable')}</Typography>
    </Stack>
  );
};

const DataProcessing = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <PaiSpinningLoader size={'small'} inline={true} />
      <Typography className={classes.processingText}>{t('analyzing')}</Typography>
    </Stack>
  );
};

const Highlight: React.FC<{ text: string; searchTerm: string }> = ({ text, searchTerm }) => {
  if (!searchTerm.trim()) return <>{text}</>;

  const regex = new RegExp(`(${_.escapeRegExp(searchTerm.trim())})`, 'gi');
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, index) => (regex.test(part) ? <mark key={index}>{part}</mark> : <React.Fragment key={index}>{part}</React.Fragment>))}
    </span>
  );
};

const CollapsibleText: React.FC<{ text: string; maxLength: number; searchTerm?: string }> = ({ text, maxLength = 100, searchTerm = '' }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setIsExpanded(!isExpanded);
  };
  const displayedText = isExpanded ? text : text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');
  return (
    <div>
      {searchTerm ? <Highlight text={displayedText} searchTerm={searchTerm} /> : displayedText}
      {text.length > maxLength && (
        <PaiLink
          text={isExpanded ? t('less') : t('more')}
          inline={!isExpanded}
          ml={!isExpanded ? '4px' : undefined}
          size={'md'}
          variant={'primary'}
          underline={'hover'}
          action={toggleExpand}
        />
      )}
    </div>
  );
};

interface Props {
  funnelId: string;
  finalContacts: FunnelContact[];
  finalContactsPage: number;
  finalContactsSearchParam: string;
  finalContactsPerPage: number;
  totalFinalContacts: number;
  finalContactsLoading: boolean;
  exportFinalContactsLoading: boolean;
  productGroup: ProductGroups;
  aiJudgementProcessing: boolean;
  profileAnalysisProcessing: boolean;
  handleChangeFinalContactsPage(event: any, value: number): void;
  handleFinalContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleClearFinalContactsSearchField(): void;
  exportCampaignContacts(): void;
  handleOpenEnrichContactsDialog(): void;
  handleOpenContactDrawer(linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined): () => void;
}

const FinalContactsTable: React.FC<Props> = ({
  funnelId,
  finalContacts,
  finalContactsPage,
  finalContactsSearchParam,
  finalContactsPerPage,
  totalFinalContacts,
  finalContactsLoading,
  exportFinalContactsLoading,
  productGroup,
  aiJudgementProcessing,
  profileAnalysisProcessing,
  handleChangeFinalContactsPage,
  handleFinalContactsSearchChange,
  handleClearFinalContactsSearchField,
  exportCampaignContacts,
  handleOpenEnrichContactsDialog,
  handleOpenContactDrawer,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const activeDrawerContact = useContactDrawerStore((state) => state.activeDrawerContact);

  // Determine displayed fields based on contact data
  const displayedFields: string[] = [];
  if (finalContacts.some((contact) => contact.email)) {
    displayedFields.push('email');
  }
  if (finalContacts.some((contact) => contact.phoneNumber)) {
    displayedFields.push('phoneNumber');
  }
  if (finalContacts.some((contact) => contact.shortSummary)) {
    displayedFields.push('shortSummary');
  }
  if (finalContacts.some((contact) => contact.aiJudgement)) {
    displayedFields.push('aiJudgement');
  }
  if (finalContacts.some((contact) => contact.aiJudgementExplanation)) {
    displayedFields.push('aiJudgementExplanation');
  }
  // Add customField if any contact has one
  if (finalContacts.some((contact) => contact.customField && contact.customField.trim() !== '')) {
    displayedFields.push('customField');
  }
  if (finalContacts.some((contact) => contact.searchName)) {
    displayedFields.push('searchName');
  }

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  const mapAiJudgementToText = (judgement: string | undefined) => {
    switch (judgement) {
      case Qualifications.A:
        return getLocalizedText('topCandidates');
      case Qualifications.B:
        return getLocalizedText('strongContenders');
      case Qualifications.C:
        return getLocalizedText('backupCandidatePool');
      case Qualifications.ERROR:
        return t('aiJudgingError');
      default:
        return '-';
    }
  };

  const searchTerms = [t('firstName'), t('lastName'), t('email'), t('jobTitle'), t('company'), t('location'), t('searchName')];

  return (
    <>
      <div className={classes.contactList}>
        <div className={classes.listHeader}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <PaiRoundTextField
                style={{ width: '300px' }}
                value={finalContactsSearchParam}
                variant={'search'}
                placeholder={t('enterSearchTermHere')}
                onClear={handleClearFinalContactsSearchField}
                onChange={handleFinalContactsSearchChange}
              />
              <PaiHelpIcon
                tooltipText={t('searchTermsAre', {
                  searchTerms: searchTerms.map((term) => `• ${term}`).join('\n'),
                })}
              />
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <PaiButton
                text={getLocalizedText('downloadData')}
                size={'small'}
                border={'rounded'}
                startIcon={<DownloadIcon />}
                disabled={_.isEmpty(finalContacts)}
                loading={exportFinalContactsLoading}
                action={exportCampaignContacts}
              />
              <PaiButton text={getLocalizedText('enrichContacts')} size={'small'} border={'rounded'} action={handleOpenEnrichContactsDialog} />
            </Stack>
          </Stack>
        </div>
        <div className={classes.listContent}>
          <TableContainer sx={{ height: '100%', width: '100%' }}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell sx={{ minWidth: '350px' }}>{t('name')}</TableCell>
                  <TableCell sx={{ minWidth: '350px' }}>{t('personalityType')}</TableCell>
                  {displayedFields.includes('aiJudgement') && <TableCell sx={{ minWidth: '350px' }}>{t('qualifiedAs')}</TableCell>}
                  <TableCell sx={{ minWidth: '350px' }}>{t('company')}</TableCell>
                  <TableCell sx={{ minWidth: '350px' }}>{t('jobTitle')}</TableCell>
                  <TableCell sx={{ minWidth: '350px' }}>{t('location')}</TableCell>
                  {displayedFields.includes('email') && <TableCell sx={{ minWidth: '350px' }}>{t('email')}</TableCell>}
                  {displayedFields.includes('searchName') && <TableCell sx={{ minWidth: '350px' }}>{t('source')}</TableCell>}
                  <TableCell sx={{ minWidth: '150px' }}>{t('linkedinUrl')}</TableCell>
                  {displayedFields.includes('phoneNumber') && <TableCell sx={{ minWidth: '350px' }}>{t('phoneNumber')}</TableCell>}
                  {displayedFields.includes('shortSummary') && <TableCell sx={{ minWidth: '350px' }}>{t('shortSummary')}</TableCell>}
                  {displayedFields.includes('customField') && <TableCell sx={{ minWidth: '350px' }}>{t('customField')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {finalContacts.map((contact, index) => (
                  <TableRow
                    key={index}
                    className={finalContactsLoading ? classes.loading : undefined}
                    onClick={
                      contact.preprocessedProfileAnalysis
                        ? handleOpenContactDrawer(contact.linkedInURL, contact.profileIds?.linkedin, funnelId)
                        : undefined
                    }
                    hover={contact.preprocessedProfileAnalysis}
                    selected={activeDrawerContact === contact.linkedInURL}
                    style={{ cursor: contact.preprocessedProfileAnalysis ? 'pointer' : undefined }}
                  >
                    <TableCell>
                      <Stack direction={'row'} alignItems={'center'} gap={1} style={{ marginTop: '2px' }}>
                        <Avatar src={''} sx={{ width: 24, height: 24, mr: 1 }} />
                        <Highlight
                          text={
                            contact.fullName?.trim() ||
                            (!_.isNil(contact.firstName) && !_.isNil(contact.lastName)
                              ? `${contact.firstName} ${contact.lastName}`
                              : getLocalizedText('linkedInContact'))
                          }
                          searchTerm={finalContactsSearchParam}
                        />
                        {contact.preprocessedProfileAnalysis ? (
                          !contact.isReviewed ? (
                            <VisibilityIcon className={classes.visibilityIcon} />
                          ) : (
                            <VisibilityIcon className={classes.checkIcon} />
                          )
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {!contact.linkedInURL && !contact.assessment ? (
                        <DataNotAvailable />
                      ) : contact.linkedInURL && !contact.assessment ? (
                        <DataProcessing />
                      ) : (
                        contact.assessment || '-'
                      )}
                    </TableCell>
                    {displayedFields.includes('aiJudgement') && <TableCell>{mapAiJudgementToText(contact.aiJudgement)}</TableCell>}
                    <TableCell>
                      {!_.isNil(contact.currentCompanies) && !_.isEmpty(contact.currentCompanies) ? (
                        <CollapsibleText
                          text={contact.currentCompanies.map((company) => (typeof company === 'string' ? company : company.companyName)).join(', ')}
                          maxLength={40}
                          searchTerm={finalContactsSearchParam}
                        />
                      ) : (
                        <DataNotAvailable />
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.occupation ? (
                        <CollapsibleText text={contact.occupation} maxLength={40} searchTerm={finalContactsSearchParam} />
                      ) : (
                        <DataNotAvailable />
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.location ? (
                        <CollapsibleText text={contact.location} maxLength={40} searchTerm={finalContactsSearchParam} />
                      ) : (
                        <DataNotAvailable />
                      )}
                    </TableCell>
                    {displayedFields.includes('email') && (
                      <TableCell>
                        {contact.email ? <Highlight text={contact.email} searchTerm={finalContactsSearchParam} /> : <DataNotAvailable />}
                      </TableCell>
                    )}
                    {displayedFields.includes('searchName') && (
                      <TableCell>
                        {contact.searchName ? (
                          <CollapsibleText text={contact.searchName} maxLength={40} searchTerm={finalContactsSearchParam} />
                        ) : (
                          <DataNotAvailable />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {contact.linkedInURL ? (
                        <PaiLink
                          text={t('open')}
                          size={'md'}
                          variant={'primary'}
                          IconComponent={<OpenInNewIcon />}
                          href={contact.linkedInURL}
                          target={'_blank'}
                        />
                      ) : (
                        <DataNotAvailable />
                      )}
                    </TableCell>
                    {displayedFields.includes('phoneNumber') && <TableCell>{contact.phoneNumber || <DataNotAvailable />}</TableCell>}
                    {displayedFields.includes('shortSummary') && <TableCell>{contact.shortSummary || <DataNotAvailable />}</TableCell>}
                    {displayedFields.includes('customField') && (
                      <TableCell>
                        {contact.customField ? <CollapsibleText text={contact.customField} maxLength={60} /> : <DataNotAvailable />}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <Stack className={classes.listFooter} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <Typography className={classes.contactCountText}>{`# ${totalFinalContacts} ${getLocalizedText('contacts')}`}</Typography>
            {aiJudgementProcessing ? (
              <Stack direction={'row'} alignItems={'center'} gap={3} ml={'16px'}>
                <PaiDotLoader size={'small'} inline={true} />
                <Typography className={cx(classes.headerText, 'primary')}>{t('qualificationInProgress')}</Typography>
              </Stack>
            ) : profileAnalysisProcessing ? (
              <Stack direction={'row'} alignItems={'center'} gap={3} ml={'16px'}>
                <PaiDotLoader size={'small'} inline={true} />
                <Typography className={cx(classes.headerText, 'primary')}>{t('profileAnalysisInProgress')}</Typography>
              </Stack>
            ) : null}
          </Stack>
          <PaiPagination
            total={totalFinalContacts}
            itemsPerPage={finalContactsPerPage}
            page={finalContactsPage}
            handleChangePage={handleChangeFinalContactsPage}
          />
        </Stack>
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactList: {
    height: '100%',
    width: '100%',
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  listHeader: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  headerText: {
    fontSize: theme.typography.pxToRem(16),
    '&.primary': {
      color: theme.palette.primary.main,
    },
  },
  listContent: {
    position: 'relative',
    height: '100%',
    padding: '8px',
    overflow: 'auto',
  },
  loading: {
    pointerEvents: 'none',
    background: theme.palette.action.hover,
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9,
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
      '& mark': {
        backgroundColor: lighten(theme.palette.secondary.main, 0.8),
      },
    },
  },
  notAvailableText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light,
  },
  processingText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light,
  },
  listFooter: {
    padding: '8px 16px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
    marginTop: 'auto',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  contactCountText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  visibilityIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.greys.light,
  },
  checkIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.success.main,
    opacity: 0.5,
  },
}));

export default FinalContactsTable;
